import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { EMAIL, LOGS, USERS } from '@kp/env';
import { LogService } from '@kp/logs';
import { injectRoot } from '@kp/utils';
import {
  catchError,
  map,
  NEVER,
  of,
  Subject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { ModalService } from './modal.service';

import type {
  HttpErrorResponse,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import type { AnonymousParsedLoginResponse, LoginToken } from '@kp/interfaces';
import type { Observable } from 'rxjs';

const SESSION_KEY = 'uid';

@Service()
class TiiaAuthService {
  readonly #modalß = inject(ModalService);

  readonly #httpBackend = new HttpClient(inject(HttpBackend));
  readonly #login$ = new Subject<boolean>();
  #token = '';
  readonly login$ = this.#login$.asObservable();
  noSession = false;
  organization = 'KP';
  userId = '';

  #saveLoginData(token: string, userId: string): void {
    const sessionKey = sessionStorage.getItem(SESSION_KEY);

    this.#token = token;
    this.userId = sessionKey && sessionKey !== 'null' ? sessionKey : userId;

    sessionStorage.setItem(SESSION_KEY, this.userId);
  }

  checkSession(): void {
    if (sessionStorage.getItem(SESSION_KEY) === null) {
      this.noSession = true;
    }
  }

  login(
    relogin = false,
    organization = this.organization,
  ): Observable<boolean> {
    const body = {
      application: 'TIIA',
      organization,
    };

    this.organization = organization;

    return this.#httpBackend
      .post<LoginToken>(`${__API_URL}/${USERS}/login`, body, {
        headers: relogin ? { 'x-user-id': this.userId } : {},
        observe: 'response',
      })
      .pipe(
        switchMap(parseResponse$),
        tap(({ token, userId }) => {
          this.#saveLoginData(token, userId);
          this.#login$.next(true);
        }),
        map(() => true),
        catchError((error: HttpErrorResponse | undefined) =>
          this.processNetworkError(error).pipe(map(() => false)),
        ),
      );
  }

  processNetworkError(
    error?: HttpErrorResponse,
    passErrors = false,
  ): Observable<HttpEvent<unknown>> {
    if (
      error?.url?.includes(LOGS) ||
      error?.url?.includes(EMAIL) ||
      passErrors
    ) {
      return throwError(() => error);
    }

    injectRoot(LogService).error(error);

    return this.#modalß
      .confirm(
        'Omlouváme se...',
        'Stala se chyba, sjednání nelze dokončit.\n\nZkuste to prosím později.',
        true,
      )
      .pipe(
        tap(() => {
          location.href = 'https://www.kb-pojistovna.cz/';
        }),
        switchMap(() => NEVER),
      );
  }

  get token(): string {
    return this.#token;
  }
}

/**
 * Gets token from response header. If token is valid it returns back the response otherwise it throw an error.
 */
function parseResponse$(
  resp: HttpResponse<LoginToken>,
): Observable<AnonymousParsedLoginResponse> {
  const headerToken = resp.headers.get('x-jwt-assertion');
  const userId = resp.headers.get('x-user-id') as string;

  return !headerToken || !resp.body
    ? throwError(() => new Error('Token is not present'))
    : of({
        token: resp.body.access_token,
        userId,
      });
}

export { TiiaAuthService };
